import { useMemo } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { AlertCircle, ListChecks } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ReadinessCard } from '@/components/dashboard/ReadinessCard'
import { useOperationsList } from '@/hooks/api/useOperationsList'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { ACTIVE_CHAINS, chainName } from '@/config/chains'
import { Badge } from '@/components/ui/badge'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { EmptyState } from '@/components/data/EmptyState'
import { StatusBadge } from '@/components/operations/StatusBadge'
import { ApiError } from '@/lib/api'
import { OPERATION_STATUSES, TERMINAL_STATUSES, type OperationStatus } from '@/lib/constants'

export const Route = createFileRoute('/')({
    component: DashboardPage,
})

function DashboardPage() {
    const { address, rolesByChain } = useOperatorSession()

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Dashboard</h1>
                <p className="text-muted-foreground text-sm">
                    System status and your role across configured chains.
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Operator identity</CardTitle>
                        <CardDescription>Wallet currently signed in.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <AddressDisplay address={address} truncate={false} />
                        <div className="space-y-1.5">
                            {ACTIVE_CHAINS.map((c) => (
                                <div
                                    key={c.id}
                                    className="border-border flex items-center justify-between rounded border px-3 py-1.5 text-sm"
                                >
                                    <span>{chainName(c.id)}</span>
                                    {rolesByChain[c.id] ? (
                                        <Badge variant="positive">on-chain OPERATOR_ROLE</Badge>
                                    ) : (
                                        <Badge variant="muted">service-signed writes</Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="text-muted-foreground text-[11px]">
                            On-chain OPERATOR_ROLE belongs to the gaming service's signer, which
                            executes chain writes through the operations queue — console actions
                            never need it on your wallet.
                        </p>
                    </CardContent>
                </Card>

                <ReadinessCard />
            </div>

            <PendingOperationsCard />

            <Card>
                <CardHeader>
                    <CardTitle>Recent failures</CardTitle>
                    <CardDescription>
                        REVERTED + FAILED operations in the last 24 hours.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <EmptyState icon={AlertCircle} title="Awaiting backend list endpoint" />
                </CardContent>
            </Card>
        </div>
    )
}

const NON_TERMINAL_STATUSES: OperationStatus[] = OPERATION_STATUSES.filter(
    (s) => !TERMINAL_STATUSES.has(s),
)

function PendingOperationsCard() {
    const query = useOperationsList(100)
    const counts = useMemo(() => {
        const all = query.data?.pages.flatMap((p) => p.operations) ?? []
        const byStatus: Partial<Record<OperationStatus, number>> = {}
        for (const op of all) {
            if (TERMINAL_STATUSES.has(op.status)) continue
            byStatus[op.status] = (byStatus[op.status] ?? 0) + 1
        }
        return byStatus
    }, [query.data])

    const total = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0)

    return (
        <Card>
            <CardHeader>
                <CardTitle>Pending operations</CardTitle>
                <CardDescription>
                    Non-terminal operations from{' '}
                    <code className="font-mono">GET /v1/operations</code>, scoped to your authorized
                    chains. Refreshes every 15s.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {query.isLoading && <Skeleton className="h-16 w-full" />}
                {query.isError && (
                    <div className="text-destructive text-sm">
                        {query.error instanceof ApiError && query.error.status === 401
                            ? 'Session expired — reload to re-authenticate with your wallet.'
                            : `Could not load operations: ${query.error instanceof Error ? query.error.message : 'unknown error'}`}
                    </div>
                )}
                {query.data && total === 0 && (
                    <div className="text-muted-foreground text-sm">No pending operations.</div>
                )}
                {query.data && total > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        {NON_TERMINAL_STATUSES.map((status) => {
                            const n = counts[status] ?? 0
                            if (n === 0) return null
                            return (
                                <div
                                    key={status}
                                    className="border-border flex items-center gap-1.5 rounded border px-2 py-1 text-sm"
                                >
                                    <StatusBadge status={status} />
                                    <span className="font-mono">{n}</span>
                                </div>
                            )
                        })}
                        <Link
                            to="/operations"
                            className="text-muted-foreground hover:text-foreground ml-auto inline-flex items-center gap-1 text-sm"
                        >
                            <ListChecks className="h-4 w-4" /> View all
                        </Link>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
